import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const ShowcaseSection = ({ title, subtitle, description, imageWebp, imageFallback, align = 'left', index, Icon }) => {
    const ref = useRef(null);            // Used for section parallax
    const dotRef = useRef(null);         // Used for dot activation logic
    const [isActive, setIsActive] = useState(false);

    // Parallax scroll progress
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"]
    });

    // Global scroll for dot activation logic
    const { scrollY } = useScroll();

    useEffect(() => {
        const unsubscribe = scrollY.on("change", () => {
            if (dotRef.current) {
                const rect = dotRef.current.getBoundingClientRect();
                // Trigger earlier: when element is in the upper 80% of the screen (enters from bottom)
                const triggerPoint = window.innerHeight * 0.8;
                // Active if the dot is ABOVE or AT the trigger line
                setIsActive(rect.top <= triggerPoint);
            }
        });
        return () => unsubscribe();
    }, [scrollY]);

    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Softer parallax on mobile to prevent overlap, strong on desktop
    const parallaxRange = isMobile ? [30, -30] : [100, -100];
    const y = useTransform(scrollYProgress, [0, 1], parallaxRange);

    const isLeft = align === 'left';

    return (
        <section ref={ref} className="py-24 md:py-32 text-white overflow-hidden relative">
            <div className="container mx-auto px-6">
                <div className={`flex flex-col md:flex-row items-center gap-12 md:gap-8 ${isLeft ? '' : 'md:flex-row-reverse'}`}>

                    {/* Text Content */}
                    <div className="flex-1 space-y-8">
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-10%" }}
                            transition={{ duration: 0.8 }}
                        // onViewportEnter removed, controlled by scroll logic now
                        >
                            {/* Icon Display */}
                            {Icon && (
                                <div className="mb-6">
                                    <Icon strokeWidth={1} className="w-12 h-12 text-white/80" />
                                </div>
                            )}

                            <h4 className="text-gray-400 uppercase tracking-[0.2em] text-sm mb-4">{subtitle}</h4>
                            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">{title}</h2>
                            <p className="text-lg text-gray-400 leading-relaxed font-light">{description}</p>


                        </motion.div>
                    </div>

                    {/* TIMELINE COLUMN - Asse Condiviso */}
                    <div className="relative w-16 md:w-24 shrink-0 mx-4 h-full hidden md:block">

                        {/* 1. LA LINEA VERTICALE */}
                        <div className="absolute left-1/2 top-0 bottom-0 w-[1px] bg-blue-500/30 transform -translate-x-1/2"></div>

                        {/* 2. IL PALLINO (WRAPPER) - Animato al passaggio */}
                        {/* OUTER DIV: Posizionamento statico (Salva il transform CSS) */}
                        <div ref={dotRef} className="absolute top-0 left-1/2 transform -translate-x-1/2 flex items-center justify-center z-10">

                            {/* INNER MOTION DIV: Animazione (Scale/Opacity) - Controllata da isActive */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0 }}
                                animate={{ opacity: isActive ? 1 : 0, scale: isActive ? 1 : 0 }}
                                transition={{ duration: 0.5, ease: "easeOut" }}
                                className="relative flex items-center justify-center"
                            >
                                {/* A. Alone Luminoso (Ping) */}
                                <div className="absolute w-8 h-8 bg-blue-500 rounded-full opacity-30 animate-ping"></div>

                                {/* B. Alone Statico (Glow) */}
                                <div className="absolute w-4 h-4 bg-blue-500 rounded-full opacity-50 blur-[2px]"></div>

                                {/* C. Il Punto Solido (Core) */}
                                <div className="relative w-2.5 h-2.5 bg-white rounded-full border border-blue-400 shadow-sm"></div>

                                {/* D. La Linea Orizzontale (Connector) */}
                                {isLeft ? (
                                    <motion.div
                                        initial={{ scaleX: 0 }}
                                        animate={{ scaleX: isActive ? 1 : 0 }}
                                        transition={{ duration: 0.5, delay: 0.2 }}
                                        className="absolute left-1 w-8 h-[1px] bg-blue-500/50 origin-left"
                                    />
                                ) : (
                                    <motion.div
                                        initial={{ scaleX: 0 }}
                                        animate={{ scaleX: isActive ? 1 : 0 }}
                                        transition={{ duration: 0.5, delay: 0.2 }}
                                        className="absolute right-1 w-8 h-[1px] bg-blue-500/50 origin-right"
                                    />
                                )}
                            </motion.div>

                        </div>

                    </div>

                    {/* Image Content */}
                    <div className="flex-1 w-full">
                        <motion.div
                            style={{ y }} // Parallax enabled (soft or strong)
                            className="relative aspect-[4/3] w-full overflow-hidden rounded-sm"
                        >
                            <picture>
                                <source srcSet={imageWebp} type="image/webp" />
                                <img
                                    src={imageFallback}
                                    alt={title}
                                    // Image becomes colorful only when isActive is true
                                    className={`w-full h-full object-cover transition-all duration-700 ease-in-out scale-110 ${isActive ? 'grayscale-0' : 'grayscale'}`}
                                    loading="lazy"
                                    width="800"
                                    height="600"
                                />
                            </picture>
                        </motion.div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default ShowcaseSection;
